import React, {useState} from 'react';
import './SupportComponent.css';

const SUPPORT_EMAIL = 'repeatableapp@gmail.com';

const FAQS: {q: string; a: string}[] = [
  {
    q: 'What is Medi Mystery?',
    a: 'Medi Mystery is an AI-powered medical training game. Read AI-generated patient stories, analyze symptoms, and deliver the correct diagnosis or treatment across single-player and multiplayer challenges. Our AI is prompted to draw only on verified, established medical literature when generating cases.',
  },
  {
    q: 'Is the app free to play?',
    a: 'Yes. Medi Mystery is free to download and play on both iOS and Android. A limited number of free challenges are available each day.',
  },
  {
    q: 'How do I get more challenges?',
    a: 'You get a set of free challenges that reset daily. If you run out before the reset, you can watch a short ad to unlock more, or subscribe for unlimited access. You can also simply come back the next day for a fresh set of cases.',
  },
  {
    q: 'The app is not working correctly. What should I do?',
    a: 'Please make sure you are running the latest version from the App Store or Google Play, then try restarting the app. If the problem continues, contact us using the form below and include your device model and OS version.',
  },
  {
    q: 'How do I request a feature or report a bug?',
    a: `We would love to hear from you. Use the contact form below, or email us directly at ${SUPPORT_EMAIL}, and describe what you would like to see or the issue you ran into.`,
  },
];

const SupportComponent: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const bodyLines = [
      message,
      '',
      '---',
      `From: ${name || 'Not provided'}`,
      `Reply to: ${email || 'Not provided'}`,
    ];

    const mailto =
      `mailto:${SUPPORT_EMAIL}` +
      `?subject=${encodeURIComponent(subject || 'Medi Mystery Support Request')}` +
      `&body=${encodeURIComponent(bodyLines.join('\n'))}`;

    window.location.href = mailto;
    setSent(true);
  };

  return (
    <div className="support">
      <div className="support-glow" />
      <div className="support-noise" />

      <section className="support-hero">
        <div className="support-inner">
          <div className="support-badge">
            <span className="material-symbols-outlined badge-icon">support_agent</span>
            Support & Help Center
          </div>
          <h1 className="support-heading">
            How can we <span className="support-heading-accent">help you?</span>
          </h1>
          <p className="support-body">
            Have a question about Medi Mystery, found a bug, or want to request a feature?
            Browse the answers below or reach out to us directly — we're here to help.
          </p>

          <a href={`mailto:${SUPPORT_EMAIL}`} className="support-email-pill">
            <span className="material-symbols-outlined">mail</span>
            {SUPPORT_EMAIL}
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="support-section">
        <div className="support-section-inner">
          <div className="support-section-header">
            <p className="support-eyebrow">Frequently Asked Questions</p>
            <h2 className="support-section-heading">Answers to common questions</h2>
          </div>

          <div className="faq-list">
            {FAQS.map((item, i) => (
              <details className="faq-item" key={i}>
                <summary className="faq-question">
                  <span>{item.q}</span>
                  <span className="material-symbols-outlined faq-chevron">expand_more</span>
                </summary>
                <p className="faq-answer">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section className="support-section" id="contact">
        <div className="support-section-inner">
          <div className="support-section-header">
            <p className="support-eyebrow">Still need help?</p>
            <h2 className="support-section-heading">Contact our support team</h2>
            <p className="support-section-subheading">
              Fill out the form below and your email app will open with the message ready to send to{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="support-inline-link">{SUPPORT_EMAIL}</a>.
              We typically respond within 1–2 business days.
            </p>
          </div>

          <form className="support-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <label className="form-field">
                <span className="form-label">Name</span>
                <input
                  className="form-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                />
              </label>

              <label className="form-field">
                <span className="form-label">Email</span>
                <input
                  className="form-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </label>
            </div>

            <label className="form-field">
              <span className="form-label">Subject</span>
              <input
                className="form-input"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="What is this about?"
              />
            </label>

            <label className="form-field">
              <span className="form-label">Message</span>
              <textarea
                className="form-input form-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe your question, bug report, or feedback in detail…"
                rows={6}
                required
              />
            </label>

            <button className="form-submit" type="submit">
              <span className="material-symbols-outlined">send</span>
              Send Message
            </button>

            {sent && (
              <p className="form-sent-note">
                Your email app should have opened with your message. If nothing happened, email us
                directly at{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="support-inline-link">{SUPPORT_EMAIL}</a>.
              </p>
            )}
          </form>
        </div>
      </section>

      <footer className="support-footer">
        <p>Medi Mystery — AI-Powered Medical Training</p>
        <p>
          Support: <a href={`mailto:${SUPPORT_EMAIL}`} className="support-inline-link">{SUPPORT_EMAIL}</a>
        </p>
      </footer>
    </div>
  );
};

export default SupportComponent;
